<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useHistoryStore, type HistoryFilter } from '@/stores/history';
import { groupByDay } from '@/lib/group-by-day';
import MxSegmented from '@/components/MxSegmented.vue';
import MxSkeleton from '@/components/MxSkeleton.vue';
import MxVideoPreview from '@/components/MxVideoPreview.vue';

const store = useHistoryStore();
const { items, loading, exhausted, filter } = storeToRefs(store);

const filters: { value: HistoryFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'video', label: 'Видео' },
  { value: 'image', label: 'Картинки' },
];

const filterModel = computed({
  get: () => filter.value,
  set: (v) => {
    filter.value = v;
  },
});

const groups = computed(() => groupByDay(items.value));

const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

function setupObserver() {
  observer?.disconnect();
  if (!sentinel.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) store.loadMore();
    },
    { rootMargin: '200px' },
  );
  observer.observe(sentinel.value);
}

onMounted(() => {
  store.loadFirst(filter.value);
  setupObserver();
});
onBeforeUnmount(() => observer?.disconnect());

watch(filter, (v) => store.loadFirst(v));
</script>

<template>
  <section class="hist">
    <MxSegmented v-model="filterModel" :options="filters" />

    <div v-if="loading && items.length === 0" class="grid">
      <MxSkeleton v-for="n in 6" :key="n" height="180px" radius="var(--r-lg)" />
    </div>
    <div v-else-if="items.length === 0" class="empty">
      <p>Тут будут ваши работы.</p>
    </div>
    <div v-else>
      <div v-for="g in groups" :key="g.label" class="day">
        <h3 class="day__lbl">{{ g.label }}</h3>
        <div class="grid">
          <RouterLink
            v-for="item in g.items"
            :key="item.id"
            :to="`/generation/${item.id}`"
            class="cell"
          >
            <MxVideoPreview
              :src="item.result_url"
              :poster="item.preview_url"
              :kind="item.type"
              aspect="3 / 4"
            />
            <span v-if="item.status === 'failed'" class="cell__fail">×</span>
          </RouterLink>
        </div>
      </div>
      <div ref="sentinel" class="sentinel" />
      <p v-if="exhausted && items.length > 0" class="end">Это всё.</p>
    </div>
  </section>
</template>

<style scoped>
.hist { padding: var(--sp-3) 0 var(--sp-6); }
.day { margin-top: var(--sp-5); }
.day__lbl {
  font-size: var(--fs-sm);
  text-transform: uppercase;
  color: var(--text-tertiary);
  letter-spacing: 0.04em;
  margin-bottom: var(--sp-3);
}
.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--sp-3);
  margin-top: var(--sp-3);
}
.cell { position: relative; }
.cell__fail {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: var(--r-full);
  background: var(--error);
  color: white;
  display: grid;
  place-items: center;
  font-weight: 700;
}
.sentinel { height: 1px; }
.empty { padding: var(--sp-12) 0; text-align: center; color: var(--text-tertiary); }
.end { color: var(--text-tertiary); text-align: center; padding: var(--sp-6) 0; }
</style>
